import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from './SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

const { FiMic, FiMicOff, FiVolume2, FiLoader, FiSquare, FiPhone, FiPhoneOff, FiCpu } = FiIcons;

const VoiceInterface = ({ isActive, onToggle }) => {
  const [isSessionActive, setIsSessionActive] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [aiResponse, setAiResponse] = useState('');
  const [status, setStatus] = useState('Ready to connect');
  
  const recognitionRef = useRef(null);
  const audioRef = useRef(null);
  const shouldRestartRef = useRef(false);
  const conversationHistoryRef = useRef([]);

  // Post-process transcript to fix common misrecognitions
  const correctTranscript = (text) => {
    // First, apply case-insensitive replacements for Plutus variations
    let corrected = text
      .replace(/\b(bluetooth|blue\s*tooth|brothers|pluto|plotus|platus|plateaus|lotus|brutus|proteus|pluton|flutist|bloatus|plautus|plotless|photos|poodles|poultice)\b/gi, 'Plutus')
      .replace(/\bhello\s+(bluetooth|blue\s*tooth)\b/gi, 'hello Plutus');
    
    return corrected;
  };

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;
      
      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript;
          } else {
            interimTranscript += result[0].transcript;
          }
        }
        
        if (interimTranscript) {
          setTranscript(correctTranscript(interimTranscript));
        }
        
        if (finalTranscript && finalTranscript.trim().length > 1) {
          const correctedText = correctTranscript(finalTranscript);
          console.log('🎤 Final transcript:', finalTranscript, '→', correctedText);
          setTranscript(correctedText);
          handleUserInput(correctedText.trim());
        }
      };
      
      recognition.onerror = (event) => {
        console.error('🎤 Recognition error:', event.error);
        if (event.error === 'not-allowed') {
          setStatus('Microphone access denied');
          setIsSessionActive(false);
        } else if (event.error !== 'aborted' && shouldRestartRef.current) {
          // Auto-restart on recoverable errors
          setTimeout(() => startListening(), 500);
        }
      };
      
      recognition.onend = () => {
        setIsListening(false);
        // Auto-restart if session is active and not speaking
        if (shouldRestartRef.current && !isSpeaking && !isProcessing) {
          setTimeout(() => startListening(), 300);
        }
      };
      
      recognitionRef.current = recognition;
    }
    
    return () => {
      shouldRestartRef.current = false;
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  // Handle user speech input
  const handleUserInput = async (text) => {
    if (!text || isProcessing || isSpeaking) return;
    
    // Pause listening while processing
    pauseListening();
    setIsProcessing(true);
    setStatus('Analyzing...');
    
    try {
      // Add to conversation history
      conversationHistoryRef.current.push({ role: 'user', content: text });
      
      // Generate AI response
      const response = await generateAIResponse(text);
      setAiResponse(response);
      
      // Add AI response to history
      conversationHistoryRef.current.push({ role: 'assistant', content: response });
      
      // Speak the response
      setStatus('Speaking...');
      await speakWithOpenAI(response);
      
    } catch (error) {
      console.error('❌ Processing error:', error);
      setStatus('Error occurred');
    } finally {
      setIsProcessing(false);
      setTranscript('');
      // Resume listening after speaking
      if (shouldRestartRef.current) {
        setStatus('Listening...');
        startListening();
      }
    }
  };

  // Generate AI response with conversation context - REAL-TIME INTELLIGENCE MODE
  const generateAIResponse = async (userMessage) => {
    try {
      // Build messages with history (keep last 6 exchanges)
      const recentHistory = conversationHistoryRef.current.slice(-12);
      
      const { data, error } = await supabase.functions.invoke('openai-chat', {
        body: {
          messages: [
            {
              role: 'system',
              content: `You are PLUTUS AI, an institutional-grade Risk Officer and Market Intelligence Voice for Snap Chart AI.

🔥 ABSOLUTE RULES - NEVER VIOLATE:

1. You NEVER mention knowledge cutoffs, training data limits, or missing information.
FORBIDDEN PHRASES (never say these):
- "As an AI..."
- "I cannot predict the future..."
- "My knowledge stops in..."
- "I don't have data..."
- "I am just a model..."
- "I cannot access real-time..."
You speak like a REAL institutional strategist working TODAY.

2. You speak as if you operate in the CURRENT market (2025 and beyond).
You reference: current macro regimes, market structures, liquidity behavior, volatility conditions, institutional psychology, dominant flows, positioning shifts.
Even WITHOUT live data, answer with: scenario-based reasoning, probabilistic forecasting, institutional frameworks, behavioral patterns, market structure logic.
You SIMULATE an institutional thought process perfectly.

3. When data is unknown, convert uncertainty into professional probabilistic analysis.
Instead of "I don't know the current BTC price" → say "Assuming BTC remains within its current volatility regime, here are the structural implications..."
Instead of "I cannot see data" → say "In the current macro environment, the dominant flows suggest..."
You ALWAYS provide value, never block the conversation.

🎯 IDENTITY:
- Senior Risk Officer with 20+ years hedge fund experience
- Discipline enforcer, market psychologist, macro-strategic analyst
- Tone: calm authority, high-intelligence, strategic mentorship, zero hesitation

🎤 VOICE STYLE:
- Calm, measured, confident - see through market noise instantly
- Structured, concise, clear with gravitas
- Intelligent, analytical, smooth, futuristic
- Never emotional, confused, or hesitant

📊 VOICE MODES:
- Advisory (default): Calm, authoritative, guiding
- Warning: Lower tone, serious, risk-focused
- Reinforcement: Supportive, confidence-building
- Tactical: Direct, sharp, numbers-focused

💎 SIGNATURE PHRASES (use naturally):
- "Discipline is your differentiator."
- "Let's protect capital first."
- "Market noise is irrelevant. Focus on structure."
- "Professionals don't chase. We position."
- "Risk comes first. Profits follow discipline."

🛡️ INTELLIGENCE FOCUS:
Always prioritize: macro conditions, liquidity environment, volatility cycles, flow dynamics, psychological traps, asymmetry, execution discipline.

📏 FORMAT:
- Keep responses to 2-3 sentences max for voice clarity
- Respond naturally as in conversation
- Always respond in English
- Sound like someone with 20+ years experience, not a model

You cover Gold (XAUUSD), Crypto (BTC, ETH, SOL, XRP, ADA, DOGE, AVAX, LINK, MATIC, DOT), and Indices (US30, NAS100, SPP500, GER30).`
            },
            ...recentHistory,
            { role: 'user', content: userMessage }
          ]
        }
      });
      
      if (error) throw error;
      
      return data?.choices?.[0]?.message?.content || 
             "Let's reassess. The connection was interrupted.";
    } catch (error) {
      console.error('❌ AI error:', error);
      return "Market conditions require patience. Let's try again.";
    }
  };

  // Speak using OpenAI TTS
  const speakWithOpenAI = async (text) => {
    try {
      setIsSpeaking(true);
      
      const { data, error } = await supabase.functions.invoke('text-to-speech', {
        body: {
          text: text,
          voice: 'onyx' // Deep authoritative voice
        }
      });
      
      if (error) throw error;
      
      if (data?.audioContent) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(data.audioContent), c => c.charCodeAt(0))],
          { type: 'audio/mp3' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        
        return new Promise((resolve) => {
          if (audioRef.current) {
            audioRef.current.pause();
          }
          
          audioRef.current = new Audio(audioUrl);
          audioRef.current.onended = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
            resolve();
          };
          audioRef.current.onerror = () => {
            setIsSpeaking(false);
            URL.revokeObjectURL(audioUrl);
            resolve();
          };
          
          audioRef.current.play().catch(() => {
            setIsSpeaking(false);
            resolve();
          });
        });
      }
    } catch (error) {
      console.error('❌ TTS error:', error);
      setIsSpeaking(false);
      
      // Fallback to browser TTS
      return new Promise((resolve) => {
        if (window.speechSynthesis) {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.lang = 'en-US';
          utterance.rate = 0.9;
          utterance.onend = () => {
            setIsSpeaking(false);
            resolve();
          };
          window.speechSynthesis.speak(utterance);
        } else {
          resolve();
        }
      });
    }
  };

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current || isListening) return;
    
    try {
      recognitionRef.current.start();
      setIsListening(true);
      console.log('🎤 Started listening');
    } catch (error) {
      if (error.name !== 'InvalidStateError') {
        console.error('🎤 Start error:', error);
      }
    }
  }, [isListening]);

  // Pause listening (temporary)
  const pauseListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      try {
        recognitionRef.current.stop();
        setIsListening(false);
      } catch (error) {
        console.error('🎤 Pause error:', error);
      }
    }
  }, [isListening]);

  // Start voice session
  const startSession = async () => {
    setIsSessionActive(true);
    shouldRestartRef.current = true;
    setStatus('Connecting...');
    conversationHistoryRef.current = [];
    
    // Initial greeting - Institutional Risk Officer style
    const greeting = "Plutus AI online. Markets are in focus. What structural insights or risk analysis do you need?";
    setAiResponse(greeting);
    setStatus('Speaking...');
    await speakWithOpenAI(greeting);
    
    setStatus('Listening...');
    startListening();
  };

  // End voice session
  const endSession = () => {
    shouldRestartRef.current = false;
    setIsSessionActive(false);
    
    if (recognitionRef.current) {
      recognitionRef.current.abort();
    }
    if (audioRef.current) {
      audioRef.current.pause();
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    
    setIsListening(false);
    setIsSpeaking(false);
    setIsProcessing(false);
    setTranscript('');
    setAiResponse('');
    setStatus('Ready to connect');
    conversationHistoryRef.current = [];
    
    onToggle && onToggle(false);
  };

  // Stop current speech
  const interruptSpeech = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    if (window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    
    if (shouldRestartRef.current) {
      setStatus('Listening...');
      startListening();
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gray-800/80 rounded-lg p-4 border border-purple-500/30"
    >
      {/* Controls */}
      <div className="flex justify-end mb-4">
        {!isSessionActive ? (
          <button
            onClick={startSession}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 shadow-lg shadow-purple-500/20 transition-all"
          >
            <SafeIcon icon={FiPhone} className="w-4 h-4" />
            <span className="text-sm font-medium">Start</span>
          </button>
        ) : (
          <button
            onClick={endSession}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/30 hover:bg-red-500/30 hover:border-red-500/50 text-red-400 transition-all"
          >
            <SafeIcon icon={FiPhoneOff} className="w-4 h-4" />
            <span className="text-sm font-medium">End</span>
          </button>
        )}
      </div>
      
      {/* Status indicator */}
      <div className="text-center mb-4">
        <span className={`text-xs px-3 py-1.5 rounded-full border ${
          isListening ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 text-cyan-400 animate-pulse' :
          isSpeaking ? 'bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border-cyan-500/30 text-cyan-400' :
          isProcessing ? 'bg-yellow-500/20 border-yellow-500/30 text-yellow-400' :
          isSessionActive ? 'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 text-cyan-400' :
          'bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border-purple-500/30 text-cyan-400'
        }`}>
          {status}
        </span>
      </div>
      
      {/* Active session UI */}
      <AnimatePresence mode="wait">
        {isSessionActive && (
          <motion.div
            key="session"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="space-y-4"
          >
            {/* Visual indicator */}
            <div className="flex justify-center">
              {isListening && !isSpeaking && !isProcessing && (
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="relative"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-full blur-xl opacity-50 animate-pulse"></div>
                  <div className="relative w-20 h-20 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border-2 border-purple-500/50 rounded-full flex items-center justify-center shadow-lg shadow-purple-500/30">
                    <SafeIcon icon={FiMic} className="w-8 h-8 text-cyan-400" />
                  </div>
                  {/* Sound waves */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 border-2 border-purple-400/30 rounded-full animate-ping"></div>
                  </div>
                </motion.div>
              )}
              
              {isSpeaking && (
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="relative cursor-pointer"
                  onClick={interruptSpeech}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-600/30 to-purple-600/30 rounded-full blur-xl opacity-50"></div>
                  <div className="relative w-20 h-20 bg-gradient-to-r from-cyan-600/30 to-purple-600/30 border-2 border-cyan-500/50 rounded-full flex items-center justify-center shadow-lg shadow-cyan-500/30">
                    <div className="flex space-x-1">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <motion.div
                          key={i}
                          className="w-1 bg-cyan-400 rounded-full"
                          animate={{
                            height: [8, 24, 8],
                          }}
                          transition={{
                            duration: 0.5,
                            repeat: Infinity,
                            delay: i * 0.1,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
              
              {isProcessing && (
                <motion.div
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-full flex items-center justify-center"
                >
                  <SafeIcon icon={FiLoader} className="w-8 h-8 text-cyan-400 animate-spin" />
                </motion.div>
              )}
            </div>
            
            {/* Current transcript */}
            {transcript && (
              <div className="bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/30 rounded-lg p-3">
                <p className="text-xs text-cyan-400 mb-1">You:</p>
                <p className="text-sm text-white">{transcript}</p>
              </div>
            )}
            
            {/* AI Response */}
            {aiResponse && (
              <div className="bg-gradient-to-r from-purple-900/30 to-cyan-900/30 rounded-lg p-3 border border-purple-500/20">
                <p className="text-xs text-purple-400 mb-1">Plutus AI:</p>
                <p className="text-sm text-gray-200">{aiResponse}</p>
              </div>
            )}
            
            {/* Instructions */}
            <p className="text-xs text-gray-500 text-center">
              {isSpeaking ? 'Tap the waves to interrupt' : 'Speak naturally - I\'m listening'}
            </p>
          </motion.div>
        )}
        
        {!isSessionActive && (
          <motion.div
            key="idle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center text-gray-400 py-4"
          >
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg shadow-purple-500/30">
              <SafeIcon icon={FiMic} className="w-7 h-7 text-cyan-400" />
            </div>
            <p className="text-sm text-gray-300">Click Start to begin an intelligent, AI-powered voice conversation.</p>
            <p className="text-xs text-gray-500 mt-1">Continuous listening • Intelligent dialogue • Natural conversation</p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default VoiceInterface;
